import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { SfAccountSnapshot, SyncStatus, RateLimit } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AccountStatusBadge } from '@/components/AccountStatusBadge';
import { SyncStatusBanner } from '@/components/SyncStatusBanner';
import { RefreshButton } from '@/components/RefreshButton';
import { formatDate } from '@/lib/utils';
import { Plus, Building2, Loader2 } from 'lucide-react';

export function ResellerDashboard() {
  const [accounts, setAccounts] = useState<SfAccountSnapshot[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    setIsLoading(true);
    try {
      const response = await api.getAccounts();
      setAccounts(response.accounts);
      setSyncStatus(response.sync_status);
      setRateLimit(response.rate_limit);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await api.refreshAccounts();
      if (response.success) {
        await loadAccounts();
      } else {
        console.error('Refresh failed:', response.error);
      }
      setRateLimit({
        remaining_attempts: response.remaining_attempts,
        reset_at: response.reset_at,
      });
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Accounts</h1>
            <p className="text-gray-600 mt-1">Manage your accounts</p>
          </div>
          <Link to="/requests/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Account
            </Button>
          </Link>
        </div>

        {syncStatus && <SyncStatusBanner syncStatus={syncStatus} />}

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Accounts</CardTitle>
                <CardDescription>
                  {accounts.length} account{accounts.length !== 1 ? 's' : ''} synced from Salesforce
                </CardDescription>
              </div>
              {rateLimit && (
                <RefreshButton
                  onClick={handleRefresh}
                  loading={isRefreshing}
                  rateLimit={rateLimit}
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
              </div>
            ) : accounts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Building2 className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No accounts found</p>
                <Link to="/requests/new" className="mt-4">
                  <Button variant="outline">Provision your first account</Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-gray-500">
                      <th className="pb-3 font-medium">Account Name</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Phone Number</th>
                      <th className="pb-3 font-medium">AI Minutes Used</th>
                      <th className="pb-3 font-medium">Last Updated</th>
                      <th className="pb-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {accounts.map((account) => (
                      <tr key={account.id} className="hover:bg-gray-50">
                        <td className="py-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {account.account_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {account.sf_account_id}
                            </p>
                          </div>
                        </td>
                        <td className="py-4">
                          <AccountStatusBadge status={account.account_status_ai} />
                        </td>
                        <td className="py-4 text-sm text-gray-600">
                          {account.assigned_phone_number || '-'}
                        </td>
                        <td className="py-4 text-sm text-gray-600">
                          {account.ai_minutes_used_current_cycle !== null
                            ? account.ai_minutes_used_current_cycle.toLocaleString()
                            : '-'}
                        </td>
                        <td className="py-4 text-sm text-gray-500">
                          {formatDate(account.updated_at)}
                        </td>
                        <td className="py-4">
                          <Link to={`/accounts/${account.sf_account_id}`}>
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
